/**
 * Represents information about the expiration status of a session.
 */
export interface ExpirationInfo {
  /** Whether the session has expired */
  isExpired: boolean;
  /** Time remaining until expiration in milliseconds, or null if not applicable */
  timeRemaining: number | null;
  /** Current status of the session */
  status: 'not_active' | 'active' | 'expired';
}

/**
 * Converts an expiration time string or Date object to a Date object.
 *
 * @param expirationTime - The expiration time to convert
 * @returns A Date object, null if expirationTime is null, or undefined if expirationTime is undefined
 */
export const getExpirationDate = (
  expirationTime?: string | Date | null
): Date | undefined | null => {
  if (expirationTime === undefined) return undefined;
  if (expirationTime === null) return null;
  return new Date(expirationTime);
};

/**
 * Gets detailed information about the expiration status of a session.
 *
 * @param expirationTime - The expiration time to check
 * @returns An ExpirationInfo object containing the expiration status details
 */
export const getExpirationInfo = (
  expirationTime?: string | Date | null
): ExpirationInfo => {
  const expirationDate = getExpirationDate(expirationTime);

  const sessionNotActive =
    expirationDate === null || expirationDate === undefined;
  if (sessionNotActive) {
    return {
      isExpired: false,
      timeRemaining: null,
      status: 'not_active',
    };
  }

  const now = new Date();
  const timeRemaining = expirationDate.getTime() - now.getTime();
  const isExpired = timeRemaining <= 0;

  return {
    isExpired,
    timeRemaining: timeRemaining > 0 ? timeRemaining : 0,
    status: isExpired ? 'expired' : 'active',
  };
};

/**
 * Checks if a session has expired.
 *
 * @param expirationTime - The expiration time to check
 * @returns True if the session has expired, false otherwise
 */
export const isExpired = (expirationTime?: string | Date | null): boolean => {
  return getExpirationInfo(expirationTime).isExpired;
};

/**
 * Gets the current status of a session.
 *
 * @param expirationTime - The expiration time to check
 * @returns The session status: 'not_active', 'active', or 'expired'
 */
export const getSessionStatus = (
  expirationTime?: string | Date | null
): ExpirationInfo['status'] => {
  return getExpirationInfo(expirationTime).status;
};

/**
 * Checks if a session is currently active.
 *
 * @param expirationTime - The expiration time to check
 * @returns True if the session is active, false otherwise
 */
export const isSessionActive = (
  expirationTime?: string | Date | null
): boolean => {
  return getExpirationInfo(expirationTime).status === 'active';
};

/**
 * Gets the time remaining until a session expires.
 *
 * @param expirationTime - The expiration time to check
 * @returns Time remaining in milliseconds, or null if not applicable
 */
export const getTimeUntilExpiration = (
  expirationTime?: string | Date | null
): number | null => {
  return getExpirationInfo(expirationTime).timeRemaining;
};
